/**
 * Membership Guard – Client-Side Enforcement
 * ===========================================
 * Intercepts cart operations and blocks/removes membership items
 * for customers with active subscriptions.
 *
 * Requires: window.BN_MembershipGuard (from membership-guard.liquid)
 */

(function () {
  'use strict';

  const MembershipGuard = {
    config: window.BN_MembershipGuard || {},
    originalFetch: window.fetch,
    notificationShown: false,
    isAddingMembership: false, // Lock flag to prevent race conditions

    /**
     * Initialize: setup interceptors
     */
    init: function () {
      // Cart protection (max 1 membership) runs for ALL users (logged in or not)
      this.setupFetchInterceptor();
      this.cleanupExcessMemberships();
      this.lockMembershipQuantities();
      this.setupCartMonitoring();

      // Active membership specific: block purchase entirely
      if (this.config.isLoggedIn && this.config.hasActiveMembership) {
        document.addEventListener('membership:loaded', this.onMembershipLoaded.bind(this));
      }
    },

    /**
     * Setup cart monitoring for drawer open, page nav, etc
     */
    setupCartMonitoring: function () {
      const self = this;

      // Monitor cart drawer opens
      document.addEventListener('cart-drawer-open', () => {
        console.log('[MembershipGuard] Cart drawer opened - checking for excess memberships');
        setTimeout(() => self.cleanupExcessMemberships(), 300);
      });

      // Monitor cart page loads
      if (window.location.pathname.includes('/cart')) {
        setTimeout(() => self.cleanupExcessMemberships(), 500);
      }

      // Listen for actual cart updates (when user interacts)
      document.addEventListener('cart-updated', () => {
        console.log('[MembershipGuard] Cart updated event - checking for excess memberships');
        self.cleanupExcessMemberships();
      });

      // Fallback: check on visible area changes (MutationObserver for cart element)
      const cartContainer = document.querySelector('[data-cart-drawer], [data-section-type*="cart"]');
      if (cartContainer) {
        const observer = new MutationObserver(() => {
          // Only cleanup if cart item count changed (avoid constant re-checks)
          self.cleanupExcessMemberships();
        });
        observer.observe(cartContainer, {
          childList: true,
          subtree: true,
          attributes: false
        });
      }
    },

    /**
     * Update config when ReCharge data arrives
     */
    onMembershipLoaded: function (event) {
      this.config = event.detail;
      console.log('[MembershipGuard] Membership data loaded:', this.config);
      
      // Re-evaluate cart if status changed
      if (this.config.hasActiveMembership || this.config.hadPreviousSubscription) {
        this.cleanupExcessMemberships();
      }
    },

    /**
     * Intercept fetch() calls to detect cart additions
     */
    setupFetchInterceptor: function () {
      const self = this;
      window.fetch = function (...args) {
        const url = args[0];
        const options = args[1] || {};
        console.log(options)
        console.log('fssssss',window.BN_MembershipGuard);
        // Check if this is a cart add request
        if (typeof url === 'string' && url.includes('/cart/add')) {
          
          let isAddingMembershipNow = false;

          // Verificăm în body-ul request-ului (FormData sau JSON)
          if (options.body) {
            let bodyString = "";
            if (options.body instanceof FormData) {
              // Pentru formulare clasice
              for (let pair of options.body.entries()) {
                if (pair[0] === 'id') bodyString += pair[1];
              }
            } else {
              // Pentru request-uri JSON (comun în teme moderne)
              bodyString = options.body.toString();
            }

            // Strategie: Dacă suntem pe pagina de produs membership, marcată cu data-product-membership="true"
            const isMembershipPage = !!document.querySelector('[data-product-membership="true"]');

            // Verificăm dacă request-ul vine de la un buton care aparține de un produs membership
            // Sau pur și simplu dacă suntem pe pagina de membership
            if (isMembershipPage) {
              isAddingMembershipNow = true;
            }
          }

          // DACĂ NU ESTE MEMBERSHIP, lăsăm fetch-ul să treacă normal fără alte verificări
          if (!isAddingMembershipNow) {
            return self.originalFetch.apply(window, args);
          }
          // Block IMMEDIATELY if already processing a cart add
          if (self.isAddingMembership) {
            console.log('[MembershipGuard] Blocking add - already processing');
            return Promise.resolve(new Response(JSON.stringify({
              status: 'blocked',
              message: 'Cart add already in progress'
            }), {
              status: 422,
              headers: { 'Content-Type': 'application/json' }
            }));
          }

          // Set lock SYNCHRONOUSLY before any async work
          self.isAddingMembership = true;

          // Check subscription status FIRST
          if (self.config.hasActiveMembership) {
            console.log('[MembershipGuard] Blocking add - customer has active membership');
            self.isAddingMembership = false;
            
            var message = 'You already have an active membership subscription. You cannot purchase another membership while your current one is active. If you wish to purchase a membership for somebody else, please purchase a gift card instead. Please contact support if you need assistance.';
            var gcUrl = self.config.giftCardUrl || '/products/bronco-nation-gift-card';
            
            self.showNotification('warning', message, gcUrl);

            return Promise.resolve(new Response(JSON.stringify({
              status: 'blocked',
              message: message.replace('purchase a gift card instead', '<a href="' + gcUrl + '" style="text-decoration: underline; font-weight: bold;">purchase a gift card instead</a>'),
              errors: {}
            }), {
              status: 422,
              headers: { 'Content-Type': 'application/json' }
            }));
          }

          if (self.config.hadPreviousSubscription && !self.config.hasActiveMembership) {
            console.log('[MembershipGuard] Blocking add - customer has expired subscription');
            self.isAddingMembership = false;
            
            var renewalUrl = self.config.renewalPortalUrl || '/tools/recurring/login/';
            var message = 'You have an expired subscription. Please go to the renewal portal to reactivate it.';
            
            self.showNotification('warning', message, renewalUrl);

            return Promise.resolve(new Response(JSON.stringify({
              status: 'blocked',
              message: message + ' <a href="' + renewalUrl + '" style="text-decoration: underline; font-weight: bold;">Go to Renewal Portal</a>',
              errors: {}
            }), {
              status: 422,
              headers: { 'Content-Type': 'application/json' }
            }));
          }

          // Check if membership already in cart
          return self.checkCartForMembership().then(hasMembership => {
            console.log(window.BN_MembershipGuard);
            if (hasMembership) {
              console.log('[MembershipGuard] Blocking add - membership already in cart');
              self.isAddingMembership = false;
              
              var message = 'You already have membership subscription added to your cart.  Only one membership purchase per account is allowed.';
              var gcUrl = self.config.giftCardUrl || '/products/bronco-nation-gift-card';
              
              self.showNotification('warning', message, gcUrl);

              return new Response(JSON.stringify({
                status: 'blocked',
                message: message + ' <a href="' + gcUrl + '" style="text-decoration: underline; font-weight: bold;">Click here to purchase a gift card.</a>',
                errors: {}
              }), {
                status: 422,
                headers: { 'Content-Type': 'application/json' }
              });
            }
            
            // No membership yet - allow add
            return self.originalFetch.apply(window, args)
              .then(response => {
                setTimeout(() => self.cleanupExcessMemberships(), 100);
                return response;
              })
              .finally(() => {
                // Release lock after response completes
                self.isAddingMembership = false;
              });
          }).catch(err => {
            self.isAddingMembership = false;
            throw err;
          });
        }

        // Otherwise, proceed with original fetch
        return self.originalFetch.apply(window, args);
      };
    },

    /**
     * Check if cart already contains a membership item
     * @returns {Promise<boolean>}
     */
    checkCartForMembership: function () {
      const self = this;
      return fetch('/cart.js', { credentials: 'include' })
        .then(r => r.json())
        .then(cart => {
          const hasMembership = cart.items.some(item => self.isMembershipItem(item));
          console.log('[MembershipGuard] Cart check - has membership:', hasMembership);
          return hasMembership;
        })
        .catch(err => {
          console.error('[MembershipGuard] Failed to check cart:', err);
          return false; // On error, allow the add (safer than blocking)
        });
    },



    /**
     * Clean up excess (multiple) memberships from cart
     */
    cleanupExcessMemberships: function () {
      const self = this;
      fetch('/cart.js', { credentials: 'include' })
        .then(r => r.json())
        .then(cart => {
          let membershipItems = [];
          let updateRequests = [];

          // Find all membership items (track index for line number)
          cart.items.forEach((item, index) => {
            if (self.isMembershipItem(item)) {
              membershipItems.push({ item, lineNumber: index + 1 }); // 1-indexed
            }
          });

          console.log('[MembershipGuard] Found', membershipItems.length, 'membership items in cart');

          // If more than 1 membership item, remove excess
          if (membershipItems.length > 1) {
            console.log('[MembershipGuard] Multiple memberships detected. Keeping first, removing others.');
            membershipItems.forEach(({ item, lineNumber }, index) => {
              if (index > 0) {
                // Remove other memberships
                console.log('[MembershipGuard] Removing membership at line', lineNumber);
                updateRequests.push({
                  line: lineNumber,
                  quantity: 0
                });
              } else if (item.quantity > 1) {
                // Reduce first membership to quantity 1
                console.log('[MembershipGuard] Reducing membership at line', lineNumber, 'from quantity', item.quantity, 'to 1');
                updateRequests.push({
                  line: lineNumber,
                  quantity: 1
                });
              }
            });
          } else if (membershipItems.length === 1 && membershipItems[0].item.quantity > 1) {

            // Single membership but quantity > 1, reduce to 1
            const { item, lineNumber } = membershipItems[0];
            console.log('[MembershipGuard] Membership quantity > 1. Reducing line', lineNumber, 'from', item.quantity, 'to 1.');
            updateRequests.push({
              line: lineNumber,
              quantity: 1
            });
          }

          // Execute updates if needed
          if (updateRequests.length > 0) {
            self.updateCart(updateRequests);
            setTimeout(() => {
              self.showNotification(
                'warning',
                self.config.alertMessageMultiple,
                self.config.giftCardUrl
              );
            }, 200);
          }
        })
        .catch(err => console.error('[MembershipGuard] Failed to cleanup cart:', err));
    },

    /**
     * Check if cart item is a membership product
     */
    isMembershipItem: function (item) {
      // Direct check: product_type
      if (item.product_type === 'PremiumMembership') {
        console.log('[MembershipGuard] Detected membership by product_type:', item.title);
        return true;
      }

      // Fallback: check selling_plan_allocation (singular!) + title
      const hasMembershipPlan = item.selling_plan_allocation && 
        item.selling_plan_allocation.selling_plan?.name?.toLowerCase().includes('delivery');
      
      const titleIndicates = 
        item.title.toLowerCase().includes('membership') ||
        item.title.toLowerCase().includes('premium');

      if (hasMembershipPlan && titleIndicates) {
        console.log('[MembershipGuard] Detected membership by plan + title:', item.title);
        return true;
      }

      return false;
    },

    /**
     * Update cart quantities using /cart/change endpoint
     */
    updateCart: function (updates) {
      const self = this;
      
      // Process each update sequentially using /cart/change endpoint
      let updateChain = Promise.resolve();
      
      updates.forEach(update => {
        updateChain = updateChain.then(() => {
          console.log('[MembershipGuard] Updating line', update.line, 'to quantity', update.quantity);
          return fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(update),
            credentials: 'include'
          })
          .then(r => r.json())
          .then(cart => {
            console.log('[MembershipGuard] Line update successful');
          })
          .catch(err => console.error('[MembershipGuard] Failed to update line:', err));
        });
      });
      
      updateChain.then(() => {
        console.log('[MembershipGuard] All cart updates completed');
      });
    },

    /**
     * Lock quantity input to max 1 for membership items
     */
    lockMembershipQuantities: function () {
      const self = this;
      
      // Cart page
      const cart = document.querySelector('[data-section-type="cart"]');
      if (cart) {
        const membershipRows = cart.querySelectorAll('[data-membership-item="true"]');
        membershipRows.forEach(row => {
          const quantityInput = row.querySelector('input[type="number"]');
          if (quantityInput) {
            quantityInput.max = 1;
            quantityInput.value = 1;
            quantityInput.disabled = true;
          }
          const increaseBtn = row.querySelector('[data-increase-quantity]');
          const decreaseBtn = row.querySelector('[data-decrease-quantity]');
          if (increaseBtn) increaseBtn.disabled = true;
          if (decreaseBtn) decreaseBtn.disabled = true;
        });
      }

      // Cart drawer
      const drawer = document.querySelector('[data-cart-drawer]');
      if (drawer) {
        const membershipRows = drawer.querySelectorAll('[data-membership-item="true"]');
        membershipRows.forEach(row => {
          const quantityInput = row.querySelector('input[type="number"]');
          if (quantityInput) {
            quantityInput.max = 1;
            quantityInput.value = 1;
            quantityInput.disabled = true;
          }
        });
      }
    },

    /**
     * Show notification using FoxTheme system
     */
    showNotification: function (type, message, giftCardUrl) {
      if (this.notificationShown) return;
      this.notificationShown = true;

      // Check if FoxTheme notification system exists
      if (window.FoxTheme && window.FoxTheme.Notification) {
        const linkText = 'Send them a gift card →';
        const fullMessage = `${message} <a href="${giftCardUrl}" style="text-decoration: underline; font-weight: bold;">${linkText}</a>`;
        
        window.FoxTheme.Notification.show(
          fullMessage,
          { type: type, duration: 5000 }
        );
      } else {
        // Fallback: simple alert or custom notification
        console.warn('[MembershipGuard]', message);
      }

      setTimeout(() => {
        this.notificationShown = false;
      }, 5000);
    },

    /**
     * Handle product page UI (show/hide membership alert, disable ATC button)
     */
    handleProductPage: function () {
      const self = this;
      
      // Only if customer has active membership
      if (!this.config.hasActiveMembership) return;

      // Find membership product forms
      const membershipForm = document.querySelector('[data-product-membership="true"]');
      if (!membershipForm) return;

      // Show membership alert
      const alertDiv = membershipForm.querySelector('[class*="membership-guard-product-alert"]');
      if (alertDiv) {
        alertDiv.style.display = 'block';
      }

      // Disable ATC button
      const atcBtn = membershipForm.querySelector('[data-membership-btn="true"], button[type="submit"]');
      if (atcBtn) {
        atcBtn.disabled = true;
        atcBtn.style.opacity = '0.5';
        atcBtn.style.pointerEvents = 'none';
        atcBtn.textContent = 'Subscription Active - Gift Card Available';
      }

      // Update alert when ReCharge data loads
      document.addEventListener('membership:loaded', function (event) {

        if (event.detail.hasActiveMembership) {
          if (alertDiv) alertDiv.style.display = 'block';
          if (atcBtn) {
            atcBtn.disabled = true;
            atcBtn.style.opacity = '0.5';
          }
        }
      });
    },

    /**
     * Handle product cards (search results, collections, etc.)
     * Disables ATC / Choose Options buttons on membership product cards
     */
    handleProductCards: function () {
      const self = this;


      if (!this.config.hasActiveMembership) return;

      const giftCardUrl = this.config.giftCardUrl || '/products/bronco-nation-gift-card';
      // alert();
      const membershipCards = document.querySelectorAll('.product-card[data-product-membership="true"]');
      membershipCards.forEach(function (card) {
        // Replace ATC / choose-options button with a gift card link
        const atcBtn = card.querySelector('.product-card__atc');
        if (atcBtn) {
          const link = document.createElement('a');
          link.href = giftCardUrl;
          link.className = atcBtn.className;
          link.style.textDecoration = 'none';
          link.innerHTML = '<span class="product-card__atc-text product-card__action-text">Send them a Gift Card!</span>';
          atcBtn.parentNode.replaceChild(link, atcBtn);
        }

        // Disable quick-view button
        const quickViewBtn = card.querySelector('[data-product-quickview]');
        if (quickViewBtn) {
          quickViewBtn.style.opacity = '0.5';
          quickViewBtn.style.pointerEvents = 'none';
        }
      });
    },
  };

  // Initialize when page ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      MembershipGuard.init();
      MembershipGuard.handleProductPage();
      MembershipGuard.handleProductCards();
    });
  } else {
    MembershipGuard.init();
    MembershipGuard.handleProductPage();
    MembershipGuard.handleProductCards();
  }

  // Re-check product cards when ReCharge live data arrives
  document.addEventListener('membership:loaded', function () {
    MembershipGuard.handleProductCards();
  });

  // Expose for debugging
  window.MembershipGuard = MembershipGuard;
})();
